import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AppLayout from './components/Layout/AppLayout';
import ErrorBoundary from './components/Common/ErrorBoundary';
import PerformanceMonitor from './components/Common/PerformanceMonitor';
import useAuthStore from './store/authStore';
import { useOneSignal } from './hooks/useOneSignal';
import { authApi } from './api/auth';
import toast from 'react-hot-toast';

// Lazy load pages
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Servicios = lazy(() => import('./pages/Servicios'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Drivers = lazy(() => import('./pages/Drivers'));
const Vehicles = lazy(() => import('./pages/Vehicles'));
const Users = lazy(() => import('./pages/Users'));
const Tabulador = lazy(() => import('./pages/Tabulador'));
const Reportes = lazy(() => import('./pages/Reportes'));
const PendientesIKE = lazy(() => import('./pages/PendientesIKE'));
const Notifications = lazy(() => import('./pages/Notifications'));

// Loading component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'var(--portal-bg)',
    color: 'var(--text)'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid rgba(255,255,255,0.1)',
        borderTop: '4px solid var(--secondary)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 16px'
      }}></div>
      <p>Cargando...</p>
    </div>
  </div>
);

// Add spin animation to CSS
const spinKeyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Inject CSS
const style = document.createElement('style');
style.textContent = spinKeyframes;
document.head.appendChild(style);

function AppContent() {
  const { init, isAuthenticated } = useAuthStore();
  const { isInitialized, userId, isSubscribed, setOnNotificationReceived } = useOneSignal();
  const navigate = useNavigate();
  
  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (isInitialized) {
      console.log('OneSignal Status:', {
        userId,
        isSubscribed,
      });
    }
  }, [isInitialized, userId, isSubscribed]);

  // Register OneSignal userId when available and user is authenticated
  useEffect(() => {
    if (isAuthenticated && isInitialized && userId) {
      // Register userId with backend
      authApi.registerOneSignalUserId(userId)
        .then((result) => {
          console.log('OneSignal register result:', result);
          // Check if registration was successful
          if (result.success !== false && (result.success === true || result.message || result.session)) {
            console.log('✅ OneSignal userId registered successfully:', userId);
            if (result.session) {
              console.log('📱 Session updated:', result.session);
            }
          } else {
            console.warn('⚠️ OneSignal userId registration failed:', result.error || 'Unknown error');
          }
        })
        .catch((error) => {
          console.error('❌ Error registering OneSignal userId:', error);
        });
    }
  }, [isAuthenticated, isInitialized, userId]);

  // Set up notification handler
  useEffect(() => {
    if (isInitialized && setOnNotificationReceived) {
      const handler = (notification) => {
        // Validate that we have a real notification
        if (!notification) {
          console.log('⚠️ Ignoring undefined/null notification');
          return;
        }
        
        console.log('📱📱📱 Notification received in handler:', notification);
        console.log('📱 Full notification object:', JSON.stringify(notification, null, 2));
        
        // Extract notification data
        const title = notification?.title || notification?.notification?.title;
        const message = notification?.body || notification?.message || notification?.notification?.body;
        const data = notification?.data || notification?.additionalData || notification?.notification?.data || {};
        
        // Only show toast if we have a valid title (not empty or default)
        if (!title || title.trim() === '' || title === 'Nueva notificación') {
          console.log('⚠️ Ignoring notification with invalid title:', title);
          return;
        }
        
        console.log('📱 Showing toast with:', { title, message, data });
        
        // Show toast notification
        toast(
          (t) => (
            <div 
              style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '4px',
                cursor: 'pointer',
                minWidth: '300px'
              }}
              onClick={() => {
                toast.dismiss(t.id);
                // Handle navigation if URL is provided in notification data
                if (data.url) {
                  navigate(data.url);
                } else if (data.route) {
                  navigate(data.route);
                }
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#fff' }}>
                {title}
              </div>
              {message && (
                <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                  {message}
                </div>
              )}
            </div>
          ),
          {
            duration: 5000,
            icon: '🔔',
            style: {
              background: '#363636',
              color: '#fff',
              borderRadius: '8px',
              padding: '12px 16px',
            },
          }
        );
      };
      
      setOnNotificationReceived(handler);
    } else {
      console.log('⚠️ Cannot set up notification handler:', {
        isInitialized,
        hasSetOnNotificationReceived: !!setOnNotificationReceived,
      });
    }
  }, [isInitialized, setOnNotificationReceived, navigate]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout>
            <Dashboard />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/servicios" element={
        <ProtectedRoute>
          <AppLayout>
            <Servicios />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/trips" element={
        <ProtectedRoute>
          <AppLayout>
            <Servicios />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/asignaciones" element={
        <ProtectedRoute>
          <AppLayout>
            <Servicios />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <AppLayout>
            <Calendar />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/drivers" element={
        <ProtectedRoute>
          <AppLayout>
            <Drivers />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/vehicles" element={
        <ProtectedRoute>
          <AppLayout>
            <Vehicles />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute requiredRole="admin">
          <AppLayout>
            <Users />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/tabulador" element={
        <ProtectedRoute>
          <AppLayout>
            <Tabulador />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/reportes" element={
        <ProtectedRoute>
          <AppLayout>
            <Reportes />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/pendientes-ike" element={
        <ProtectedRoute>
          <AppLayout>
            <PendientesIKE />
          </AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <AppLayout>
            <Notifications />
          </AppLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <AppContent />
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
